import { useState } from 'react';
import { type AxiosError } from 'axios';
import { Alert, Modal, SearchBar, Button } from '@shared/components';
import { readApiError, DEFAULT_ERROR_MESSAGE } from '@/services/api';
import { useAssignableGroupsQuery } from '../hooks/useAssignableGroupsQuery';
import { useAssignUserToGroupsMutation } from '../hooks/useAssignUserToGroupsMutation';
import { SecurityGroupPermissionsPicker } from './SecurityGroupPermissionsPicker';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
}

const MAX_GROUPS = 200;
const HEADING_ID = 'assign-user-to-groups-heading';

export function AssignUserToGroupsModal({ isOpen, onClose, userId, userName }: Props) {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [defaults, setDefaults] = useState({ can_quote: false, can_invoice: false, can_sell: false });
  const [defaultsTouched, setDefaultsTouched] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [outOfScope, setOutOfScope] = useState<{ ids: string[]; message: string | null }>({
    ids: [],
    message: null,
  });

  const assignableQuery = useAssignableGroupsQuery({
    userId,
    search: search.length >= 2 ? search : undefined,
    per_page: 200,
  });
  const assignMutation = useAssignUserToGroupsMutation(userId);

  const availableGroups = assignableQuery.data?.data ?? [];

  const toggleSelect = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleDefault = (key: 'can_quote' | 'can_invoice' | 'can_sell', value: boolean) => {
    setDefaults((prev) => ({ ...prev, [key]: value }));
    setDefaultsTouched(true);
  };

  const handleSubmit = () => {
    setSubmitError(null);
    setSuccessMessage(null);
    setOutOfScope({ ids: [], message: null });

    const payload = defaultsTouched
      ? { group_ids: Array.from(selectedIds), default_permissions: defaults }
      : { group_ids: Array.from(selectedIds) };

    assignMutation.mutate(payload, {
      onSuccess: (result) => {
        const msg =
          result.skipped_count > 0
            ? `Assigned to ${result.assigned_count} group(s) (${result.skipped_count} skipped — already a member)`
            : `Assigned to ${result.assigned_count} group(s)`;
        setSuccessMessage(msg);
        setTimeout(onClose, 1000);
      },
      onError: (err: unknown) => {
        // The shared interceptor rejects an ApiError wrapper carrying no Axios
        // marker, so this reads `.response` structurally rather than gating on it.
        const response = (err as AxiosError<{ out_of_scope_ids?: unknown; message?: unknown }>)?.response;

        if (response?.status === 422 && Array.isArray(response.data?.out_of_scope_ids)) {
          const serverMessage = response.data?.message;
          setOutOfScope({
            ids: response.data.out_of_scope_ids as string[],
            message:
              typeof serverMessage === 'string' && serverMessage.trim() !== '' ? serverMessage : null,
          });
        } else {
          const { message } = readApiError(response?.data);
          setSubmitError(
            message === DEFAULT_ERROR_MESSAGE ? 'Failed to assign security groups.' : message,
          );
        }
      },
    });
  };

  const overCap = selectedIds.size > MAX_GROUPS;
  const submitDisabled =
    selectedIds.size === 0 || overCap || availableGroups.length === 0 || assignMutation.isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-2xl" ariaLabelledBy={HEADING_ID}>
      <div className="p-6 space-y-4">
        <h2 id={HEADING_ID} className="text-xl font-semibold">
          Assign Security Groups to {userName}
        </h2>

        {assignableQuery.isLoading ? (
          <p className="text-mp-muted">Loading security groups...</p>
        ) : assignableQuery.isError ? (
          <Alert variant="error">
            <span>Failed to load security groups. Please close and retry.</span>
          </Alert>
        ) : (
          <>
            {/* SearchBar stays mounted once the query succeeds so the admin can
                always clear or change the filter, even when a search yields no
                matches. */}
            <SearchBar
              searchTerm={search}
              setSearchTerm={setSearch}
              placeholder="Search security groups"
            />

            <SecurityGroupPermissionsPicker
              groups={availableGroups}
              selectedIds={selectedIds}
              onToggleGroup={toggleSelect}
              defaults={defaults}
              onToggleDefault={toggleDefault}
              maxGroups={MAX_GROUPS}
              emptyMessage={
                search.length >= 2
                  ? 'No security groups match your search.'
                  : "No security groups are assignable to this user's home dealer."
              }
            />
          </>
        )}

        {outOfScope.ids.length > 0 && (
          <Alert variant="error">
            <span>
              {outOfScope.message ??
                `${outOfScope.ids.length} selected group(s) are not assignable to this user's home dealer.`}{' '}
              Please deselect the {outOfScope.ids.length} affected group(s) and try again.
            </span>
          </Alert>
        )}

        {submitError && (
          <Alert variant="error">
            <span>{submitError}</span>
          </Alert>
        )}

        {successMessage && (
          <Alert variant="success" role="status">
            <span>{successMessage}</span>
          </Alert>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitDisabled}>
            {assignMutation.isPending ? 'Assigning...' : 'Assign'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
